import { z } from "zod";
import { DateOnly, Money } from "./common.ts";

export const FEE_RECURRENCES = ["monthly", "quarterly", "annual", "one_time"] as const;
export const FeeInput = z.object({
  companyId: z.string().uuid(),
  billToCompanyId: z.string().uuid().nullable().optional(),
  billName: z.string().min(1).max(200),
  chargeType: z.string().max(40),
  recurrence: z.enum(FEE_RECURRENCES).default("monthly"),
  paymentPlan: z.enum(["single", "instalments"]).default("single"),
  instalments: z.number().int().min(2).max(24).nullable().optional(),
  paymentTermsDays: z.number().int().min(0).max(180).nullable().optional(),
  startDate: DateOnly,
  endDate: DateOnly.nullable().optional(),
  amount: Money,
  reviewDueOn: DateOnly.nullable().optional(),
  active: z.boolean().default(true),
  notes: z.string().max(2000).nullable().optional(),
});
export const FeeUpdateInput = FeeInput.partial().omit({ companyId: true });

export const ChargeCreateInput = z.object({
  companyId: z.string().uuid(),
  billToCompanyId: z.string().uuid().nullable().optional(),
  billToPersonId: z.string().uuid().nullable().optional(),
  kind: z.string().max(40),
  amount: Money,
  description: z.string().max(400),
  serviceDate: DateOnly.optional(),
  dueDate: DateOnly.nullable().optional(),
});
export const ChargeQuery = z.object({
  companyId: z.string().uuid().optional(),
  billTo: z.string().uuid().optional(),
  billed: z.string().optional(),
  paid: z.string().optional(),
  confirmed: z.string().optional(),
  unbilled: z.string().optional(),
});

export const InvoiceRunInput = z.object({
  companyId: z.string().uuid().nullable().optional(),
  period: z.string().regex(/^\d{4}-\d{2}$/).optional(),
  dryRun: z.boolean().default(true),
});
export const FeeRunInput = z.object({ period: z.string().regex(/^\d{4}-\d{2}$/) });

export const RecordPaymentInput = z.object({
  amount: Money,
  method: z.enum(["card", "ach", "check", "other"]).default("check"),
  reference: z.string().max(120).nullable().optional(),
  receivedOn: DateOnly.optional(),
});
export const ChargeInvoiceInput = z.object({ paymentMethodId: z.string().uuid().optional(), amount: Money.optional() });

export const PaymentMethodInput = z.object({
  companyId: z.string().uuid().optional(),
  kind: z.enum(["card", "ach"]),
  /** the Sola token the client-side widget produced; the platform never sees the number */
  token: z.string().min(4).max(200),
  brand: z.string().max(40).nullable().optional(),
  last4: z.string().regex(/^\d{4}$/),
  expiry: z.string().regex(/^\d{2}\/\d{2}$/).nullable().optional(),
  label: z.string().max(80).nullable().optional(),
  makeDefault: z.boolean().default(true),
});

export const AppointmentInput = z.object({
  companyId: z.string().uuid(),
  locationType: z.enum(["facility", "plant"]).nullable().optional(),
  locationId: z.string().uuid().nullable().optional(),
  personId: z.string().uuid(),
  workKind: z.string().max(40),
  serviceFrom: DateOnly.nullable().optional(),
  serviceTo: DateOnly.nullable().optional(),
  note: z.string().max(2000).nullable().optional(),
});
export const AppointmentSubmitInput = z.object({
  billDate: DateOnly.optional(),
  dayFee: Money.default(0),
  mileageMiles: z.number().min(0).max(5000).default(0),
  expenses: Money.default(0),
  expenseNote: z.string().max(1000).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
